import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AppNav from '../components/AppNav';
import { apiClient } from '../api';
import { useOnlineUsers } from '../hooks/useOnlineUsers';
import { Bookmark, MapPin, User, Heart } from 'lucide-react';

const Favorites = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { online } = useOnlineUsers();

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get('/favorites/me');
        setProfiles(res.data);
      } catch (err) {
        console.error('Failed to load favorites:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  let content;
  if (loading) {
    content = (
      <div className="flex justify-center py-16">
        <div className="w-10 h-10 border-4 border-fnl-pink border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  } else if (profiles.length === 0) {
    content = (
      <div className="text-center py-20">
        <Bookmark className="w-16 h-16 text-fnl-pink/40 mx-auto mb-4" />
        <p className="text-white text-xl font-semibold mb-1">No favorites yet</p>
        <p className="text-purple-300 mb-6">Tap the Favorites button on any profile to save them here.</p>
        <Link
          to="/browse"
          className="inline-flex items-center gap-2 px-6 h-11 bg-fnl-pink hover:bg-fnl-pink text-white font-semibold rounded-full btn-shadow-pink"
        >
          <Heart className="w-4 h-4" /> Browse Members
        </Link>
      </div>
    );
  } else {
    content = (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
        {profiles.map((p) => (
          <Link
            key={p.user_id}
            to={`/profile/${p.user_id}`}
            className="group bg-fnl-purple/60 rounded-2xl overflow-hidden border border-purple-900/40 card-hover"
          >
            <div className="aspect-[3/4] relative bg-fnl-purple-light overflow-hidden">
              {p.photo_base64 ? (
                <img
                  src={p.photo_base64}
                  alt={p.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="w-20 h-20 text-fnl-pink/50" />
                </div>
              )}
              {online.has(p.user_id) && (
                <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-black/55 backdrop-blur-sm px-2 py-1 rounded-full">
                  <span className="w-2 h-2 rounded-full bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.9)] animate-pulse"></span>
                  <span className="text-white text-[10px] font-semibold uppercase tracking-wider">Online</span>
                </div>
              )}
              <div className="absolute top-3 left-3 w-8 h-8 rounded-full bg-fnl-pink/95 flex items-center justify-center shadow-lg">
                <Bookmark className="w-4 h-4 fill-white text-white" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-fnl-dark via-transparent to-transparent" />
              <div className="absolute bottom-3 left-3 right-3 text-white">
                <div className="font-bold text-lg leading-tight">{p.name}, {p.age}</div>
                {p.location && (
                  <div className="text-xs text-purple-200 flex items-center gap-1 mt-0.5">
                    <MapPin className="w-3 h-3" />{p.location}
                  </div>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-fnl-dark">
      <AppNav />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
              <Bookmark className="w-7 h-7 fill-fnl-pink text-fnl-pink" />
              Favorites
            </h1>
            <p className="text-purple-200 mt-1">{profiles.length > 0 ? `${profiles.length} saved profile${profiles.length === 1 ? '' : 's'}` : 'Members you save will appear here.'}</p>
          </div>
        </div>
        {content}
      </div>
    </div>
  );
};

export default Favorites;
